import axios from 'axios'
import { Message, Loading } from 'element-ui'
import Vue from 'vue'

/**
 * 统一 HTTP 请求配置
 * 支持多个 axios 实例，每个实例可独立配置
 */

// Loading 状态管理
let loading = null
let loadingNum = 0

/**
 * 开启加载动画
 * @param {string} text - 加载提示文字
 */
function startLoading(text = '拼命加载中...') {
	if (loadingNum === 0) {
		loading = Loading.service({
			lock: true,
			text: text,
			background: 'rgba(255,255,255,0.5)',
			target: document.querySelector('.content,.content1'),
		})
	}
	loadingNum++
}

/**
 * 关闭加载动画
 */
function endLoading() {
	loadingNum--
	if (loadingNum <= 0 && loading) {
		loading.close()
		loading = null
	}
}

/**
 * 创建 axios 实例
 * @param {Object} config - axios 配置
 * @returns {Object} axios 实例
 */
function createAxiosInstance(config) {
	const instance = axios.create({
		responseType: 'json',
		// withCredentials: true,
		timeout: 3 * 60 * 1000,
		...config
	})

	// 请求拦截器
	instance.interceptors.request.use(
		requestConfig => {
			const { useLoading = true, loadingText = '拼命加载中...' } = requestConfig

			// 开启加载动画
			if (useLoading) {
				startLoading(loadingText)
			}

			// 添加认证信息
			const authType = requestConfig.authType || 'user_token'
			if (authType === 'user_token') {
				const token = sessionStorage.getItem('user_token') || ''
				if (requestConfig.method === 'post') {
					requestConfig.data = requestConfig.data || {}
					requestConfig.data['user_token'] = token
				} else {
					requestConfig.params = requestConfig.params || {}
					requestConfig.params['user_token'] = token
				}
				requestConfig.headers = {
					'Content-Type': 'application/x-www-form-urlencoded'
				}
			} else if (authType === 'oauth2') {
				const tokenType = sessionStorage.getItem('tokenType') || ''
				const tokenValue = sessionStorage.getItem('tokenValue') || ''
				let authorization = tokenType + ' ' + tokenValue

				// OAuth2 客户端凭证模式特殊处理
				if (requestConfig.url && requestConfig.url.includes('authorization/authorization/oauth2/token?grant_type=client_credentials')) {
					authorization = 'Basic ZXh0ZXJuYWw6MTIzNDU2'
				}
				requestConfig.headers = {
					'Authorization': authorization
				}
			}

			return requestConfig
		},
		error => {
			return Promise.reject(error)
		}
	)

	// 响应拦截器
	instance.interceptors.response.use(
		response => {
			const { useLoading = true } = response.config
			if (useLoading) {
				endLoading()
			}

			// Blob 类型直接返回
			if (response.config.responseType === 'blob') {
				return Promise.resolve(response.data)
			}

			// 根据响应字段处理
			const { status, code } = response.data
			const responseCode = status || code

			if (response.status === 200) {
				if (responseCode == 200) {
					return Promise.resolve(response.data.data)
				} else if (responseCode == 400 || responseCode == 401) {
					const errorMsg = response.data.message || response.data.data || '操作失败'
					Message.error(errorMsg)
					return Promise.reject(errorMsg)
				}else if(responseCode == 888){
					Message.error(response.data.message)
					return Promise.reject(responseCode)
				} else {
					Message.error('程序错误')
					return Promise.reject('程序错误')
				}
			}
		},
		error => {
			const { useLoading = true } = error.config || {}
			if (useLoading) {
				endLoading()
			}

			// 处理 401 错误
			if (error.response&&error.response.status === 401) {
				return Promise.reject(error.response.data)
			}

			return Promise.reject(error)
		}
	)

	return instance
}

/**
 * API 实例配置
 */
const apiInstances = {
	// 主 API 实例
	main: createAxiosInstance({
		baseURL: process.env.API_ROOT,
		authType: 'user_token',
		useLoading: true
	}),

	// KADIAO API 实例
	kadiao: createAxiosInstance({
		baseURL: process.env.API_KADIAO,
		authType: 'oauth2',
		useLoading: false
	}),

	// 萤石云 API 实例
	ys7: createAxiosInstance({
		baseURL: 'https://open.ys7.com/',
		authType: 'none',
		useLoading: false
	}),
}

// 默认导出主 API 实例
export default apiInstances.main

// 导出所有实例
export const { kadiao, ys7, print } = apiInstances

/**
 * 兼容 vue-resource 的 POST 请求封装
 * @param {string} url - 请求地址
 * @param {Object} params - 请求参数
 * @param {boolean} load - 是否显示加载动画
 * @returns {Promise}
 */
export function post(url, params = {}, load = true) {
	return new Promise((resolve, reject) => {
		const token = sessionStorage.getItem('user_token') || ''
		if (!params['user_token']) {
			params['user_token'] = token
		}

		apiInstances.main.post(url, params, { useLoading: load })
			.then(data => {
				resolve(data)
			})
			.catch(error => {
				// 处理 888 状态码（登录失效）
				if (error == 888) {
					sessionStorage.removeItem('user_token')
					sessionStorage.removeItem('pop_password_is')
					import('@/permission').then(({ default: router }) => {
						router.push({path:'/login'})
					}).catch(() => {
						window.location.href = '/login'
					})
				} else if (error) {
					Message.error(error)
				}
				reject(error)
			})
	})
}

/**
 * 兼容 vue-resource 的 GET 请求封装
 * @param {string} url - 请求地址
 * @param {Object} params - 请求参数
 * @param {boolean} load - 是否显示加载动画
 * @returns {Promise}
 */
export function get(url, params = {}, load = true) {
	return new Promise((resolve, reject) => {
		const token = sessionStorage.getItem('user_token') || ''
		if (!params['user_token']) {
			params['user_token'] = token
		}

		apiInstances.main.get(url, { params, useLoading: load })
			.then(data => {
				resolve(data)
			})
			.catch(error => {
				// 处理 888 状态码（登录失效）
				if (error == 888) {
					sessionStorage.removeItem('user_token')
					sessionStorage.removeItem('pop_password_is')
					import('@/permission').then(({ default: router }) => {
						router.push({path:'/login'})
					}).catch(() => {
						window.location.href = '/login'
					})
				} else if (error) {
					Message.error(error)
				}
				reject(error)
			})
	})
}


// WEBPACK FOOTER //
// ./src/utils/axios_config.js